import {
  ApplyDiscountCodeForm,
  CreateTransactionForm,
  useMyUserQuery,
} from "@ac-expo/api";
import { GetUserBasket } from "@ac-expo/api-types";
import {
  Button,
  errorRingClassName,
  errorTextClassName,
  Form,
  FormCheckbox,
  FormInput,
  FormSelect,
  Input,
  Svg,
} from "@ac-expo/ui-components";
import {
  CartRefusal,
  country,
  getGuestEmailLockMessage,
  getPartialDiscountCodeMessage,
  hasGroupTicketInCart,
  isDiscountCodePartiallyApplied,
} from "@ac-expo/utils";
import { LocalCartItem } from "@ac-expo/hooks";
import { Checkbox } from "@ac-expo/ui-components";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CreditCardIcon } from "../../icons";
import CheckoutRefusals from "./CheckoutRefusals";
import { ProfesionnalMappingValue } from "@web-app/src/hooks/useConsent";
import { uniqBy } from "lodash";
import {
  guestIdentityFields,
  guestIdentitySchema,
  GuestIdentityField,
} from "./utils";

type TransactionFormValues = CreateTransactionForm & {
  email?: string;
  firstName?: string;
  lastName?: string;
};

interface CheckoutCartProps {
  transactionForm: UseFormReturn<TransactionFormValues>;
  discountCodeForm: UseFormReturn<ApplyDiscountCodeForm>;
  cart?: GetUserBasket;
  cartItems: LocalCartItem[];
  organisersMapping?: Map<string, ProfesionnalMappingValue>;
  structuresMapping?: Map<string, ProfesionnalMappingValue>;
  isPaid: boolean;
  refusals: CartRefusal[];
  onBackToCart: () => void;
  onApplyDiscountCode: () => void;
  onRemoveDiscountCode: (discountCodeValue: string) => void;
  isApplyingDiscount: boolean;
  showGuestIdentityErrors: boolean;
}

function CheckoutCart({
  transactionForm,
  discountCodeForm,
  cart,
  cartItems,
  organisersMapping,
  structuresMapping,
  isPaid,
  refusals,
  onBackToCart,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  isApplyingDiscount,
  showGuestIdentityErrors,
}: CheckoutCartProps) {
  const { data: userData, isLoading: isUserLoading } = useMyUserQuery();
  // While the profile is still loading, a logged-in visitor must not be treated as a guest
  const isNotLoggedIn = !isUserLoading && userData === undefined;
  const isGuestCheckout = isNotLoggedIn || Boolean(userData?.isGuestCheckout);
  const [useSameNameForBilling, setUseSameNameForBilling] = useState(true);
  const hasAppliedDiscount = (cart?.appliedDiscountCodes?.length ?? 0) > 0;
  const emailLockMessage = getGuestEmailLockMessage({
    hasAppliedDiscount,
    hasGroupTicket: hasGroupTicketInCart(cartItems),
  });

  const discountCodeInput = discountCodeForm.watch("discountCode");
  const applyPromoCode = useCallback(() => {
    onApplyDiscountCode();
  }, [onApplyDiscountCode]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        applyPromoCode();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [applyPromoCode, discountCodeInput]);

  const organisers = transactionForm.watch("organisers");
  const structures = transactionForm.watch("structures");

  const discountCodeRegister = discountCodeForm.register("discountCode");
  const guestEmail = transactionForm.watch("email");
  const guestFirstName = transactionForm.watch("firstName");
  const guestLastName = transactionForm.watch("lastName");

  // Live re-validation only once the visitor has tried to pay or apply a promo code:
  // before that, the empty identity fields must not be painted red on arrival.
  useEffect(() => {
    if (!isGuestCheckout || !showGuestIdentityErrors) {
      guestIdentityFields.forEach((field) => {
        transactionForm.clearErrors(field);
      });
      return;
    }

    const result = guestIdentitySchema.safeParse({
      email: guestEmail ?? "",
      firstName: guestFirstName ?? "",
      lastName: guestLastName ?? "",
    });

    const issuesByField = new Map<GuestIdentityField, string>();
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const field = issue.path[0];
        if (
          field === "email" ||
          field === "firstName" ||
          field === "lastName"
        ) {
          issuesByField.set(field, issue.message);
        }
      });
    }

    guestIdentityFields.forEach((field) => {
      const message = issuesByField.get(field);
      const currentMessage =
        transactionForm.getFieldState(field).error?.message;

      if (!message) {
        if (currentMessage) {
          transactionForm.clearErrors(field);
        }
        return;
      }

      if (currentMessage !== message) {
        transactionForm.setError(field, { message });
      }
    });
  }, [
    guestEmail,
    guestFirstName,
    guestLastName,
    isGuestCheckout,
    showGuestIdentityErrors,
    transactionForm,
  ]);

  useEffect(() => {
    if (!isGuestCheckout || !userData) {
      return;
    }

    const prefills: Array<[GuestIdentityField, string | null | undefined]> = [
      ["email", userData.email],
      ["firstName", userData.firstName],
      ["lastName", userData.lastName],
    ];

    prefills.forEach(([field, value]) => {
      if (!value) {
        return;
      }

      const existingValue = transactionForm.getValues(field);

      if (existingValue) {
        return;
      }

      transactionForm.setValue(field, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [isGuestCheckout, transactionForm, userData]);

  useEffect(() => {
    if (!isGuestCheckout || !isPaid || !useSameNameForBilling) {
      return;
    }

    const currentBillingFirstName =
      transactionForm.getValues("address.firstName");
    const currentBillingLastName =
      transactionForm.getValues("address.lastName");

    if (guestFirstName && currentBillingFirstName !== guestFirstName) {
      transactionForm.setValue("address.firstName", guestFirstName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (guestLastName && currentBillingLastName !== guestLastName) {
      transactionForm.setValue("address.lastName", guestLastName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    isGuestCheckout,
    isPaid,
    useSameNameForBilling,
    guestFirstName,
    guestLastName,
    transactionForm,
  ]);

  return (
    <div className="flex flex-col gap-4 overflow-y-auto">
      <CheckoutRefusals refusals={refusals} onBackToCart={onBackToCart} />

      {isPaid ? (
        <>
          <div className="flex grow flex-col gap-2 rounded-lg bg-white p-6">
            <p className="font-bold">Codes promos</p>
            <div className="w-full max-w-sm">
              <div className="flex items-center space-x-2">
                <Input
                  placeholder="Code"
                  className={
                    discountCodeForm.formState.errors.discountCode
                      ? errorRingClassName
                      : undefined
                  }
                  {...discountCodeRegister}
                  onChange={(e) => {
                    discountCodeRegister.onChange(e);
                    if (discountCodeForm.formState.errors.discountCode) {
                      discountCodeForm.clearErrors("discountCode");
                    }
                  }}
                />
                <Button onClick={applyPromoCode} disabled={isApplyingDiscount}>
                  Valider
                </Button>
              </div>
              {discountCodeForm.formState.errors.discountCode?.message && (
                <p className={`${errorTextClassName} mt-1`}>
                  {discountCodeForm.formState.errors.discountCode?.message}
                </p>
              )}
            </div>
            {cart && cart.appliedDiscountCodes.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {uniqBy(cart.appliedDiscountCodes, "code").map(
                    (discountCode) => (
                      <div
                        key={discountCode.id}
                        className="inline-flex w-fit flex-wrap items-center whitespace-nowrap rounded-lg border py-[2px] pl-[9px] pr-1 text-sm"
                      >
                        Code#{discountCode.code}
                        <button
                          onClick={() => onRemoveDiscountCode(discountCode.code)}
                          className="ml-2"
                        >
                          <Svg name="close" alt="fermer" className="size-4" />
                        </button>
                      </div>
                    )
                  )}
                </div>
                {uniqBy(cart.appliedDiscountCodes, "code")
                  .filter(isDiscountCodePartiallyApplied)
                  .map((discountCode) => (
                    <p key={discountCode.id} className="text-xs text-gray-500">
                      {getPartialDiscountCodeMessage(discountCode)}
                    </p>
                  ))}
              </div>
            )}
          </div>

          <div className="flex grow flex-col gap-4 rounded-lg bg-white p-6">
            <p className="font-bold">Mode de paiement</p>
            <div className="flex select-none items-center gap-2 rounded-lg border px-4 py-3">
              <CreditCardIcon width={20} height={20} />
              <span className="text-sm">Carte bancaire</span>
            </div>
          </div>
        </>
      ) : null}

      <div className="flex grow flex-col gap-2 rounded-lg bg-white p-6">
        <Form {...transactionForm}>
          {isGuestCheckout ? (
            <>
              <p className="font-bold">Identité</p>
              <FormInput
                control={transactionForm.control}
                name="email"
                label="Email"
                placeholder="Email"
                type="email"
                disabled={emailLockMessage !== null}
              />
              {emailLockMessage && (
                <p className="text-xs text-gray-500">{emailLockMessage}</p>
              )}
              <FormInput
                control={transactionForm.control}
                name="lastName"
                label="Nom"
                placeholder="Nom"
                type="text"
              />
              <FormInput
                control={transactionForm.control}
                name="firstName"
                label="Prénom"
                placeholder="Prénom"
                type="text"
              />
            </>
          ) : null}
          {isPaid ? (
            <>
              <p className="font-bold">Facturation</p>
              {isGuestCheckout && (
                <div className="flex items-center gap-2 text-sm">
                  <Checkbox
                    id="useSameNameForBilling"
                    checked={useSameNameForBilling}
                    onCheckedChange={(checked) =>
                      setUseSameNameForBilling(checked === true)
                    }
                  />
                  <label
                    htmlFor="useSameNameForBilling"
                    className="cursor-pointer"
                  >
                    Utiliser le même nom et prénom pour la facturation
                  </label>
                </div>
              )}
              {(!isGuestCheckout || !useSameNameForBilling) && (
                <>
                  <FormInput
                    control={transactionForm.control}
                    name="address.lastName"
                    label="Nom"
                    placeholder="Nom"
                    type="text"
                  />
                  <FormInput
                    control={transactionForm.control}
                    name="address.firstName"
                    label="Prénom"
                    placeholder="Prénom"
                    type="text"
                  />
                </>
              )}
              <FormInput
                control={transactionForm.control}
                name={"address.address1"}
                label={"Adresse postale"}
                className={"grow"}
                placeholder={"Adresse"}
              />
              <FormInput
                control={transactionForm.control}
                name={"address.address2"}
                label={"Complément d'adresse"}
                className={"grow"}
                placeholder={"Complément d'adresse"}
              />
              <div className={"flex w-full grow items-end gap-2"}>
                <FormInput
                  control={transactionForm.control}
                  name={"address.city"}
                  className={"grow"}
                  label={"Ville"}
                  placeholder={"Ville"}
                />
                <FormInput
                  control={transactionForm.control}
                  name={"address.zipCode"}
                  className={"grow"}
                  label={"Code Postal"}
                  placeholder={"Code Postal"}
                />
              </div>
              {/* TODO(ali): is using old form for countries, it might break some logics in backend if it's changed */}
              <FormSelect
                control={transactionForm.control}
                name="address.countryCode"
                label="Pays"
                items={country.map((item) => ({
                  value: item["country-code"],
                  label: item.name,
                }))}
              />
            </>
          ) : null}
          <FormCheckbox
            control={transactionForm.control}
            name="acceptedTerms"
            className="mt-2"
            checkboxClassNames={
              transactionForm.getFieldState("acceptedTerms").error
                ? "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background rounded-[5px] ring-1 focus-visible:ring-offset-1"
                : undefined
            }
            labelClassName="text-sm"
            labelContent={
              <p>
                J&apos;ai lu et j&apos;accepte les{" "}
                <a
                  href="/cgv"
                  className="text-blue-400 underline"
                  target="_blank"
                >
                  conditions générales de vente
                </a>
                {isGuestCheckout && (
                  <>
                    , les{" "}
                    <a
                      href="/cgu"
                      className="text-blue-400 underline"
                      target="_blank"
                    >
                      conditions générales d&apos;utilisation
                    </a>
                  </>
                )}{" "}
                et j&apos;ai pris connaissance de la{" "}
                <a
                  className="text-blue-400 underline"
                  href="/politique-de-confidentialite"
                  target="_blank"
                >
                  politique de confidentialité
                </a>{" "}
                de Connect&apos;Events
              </p>
            }
          />

          {organisers &&
            organisers?.map((organiser, index) => {
              const currentOrganiser = organisersMapping?.get(organiser.id);

              if (!currentOrganiser) {
                return null;
              }

              return (
                <FormCheckbox
                  key={organiser.id}
                  control={transactionForm.control}
                  name={`organisers.${index}.hasConsented`}
                  className="mt-2"
                  labelClassName="text-sm"
                  labelContent={
                    <p>
                      En cochant cette case, j&apos;accepte de recevoir des
                      offres promotionnelles par courrier électronique de la
                      part de {currentOrganiser.name} et que mes données
                      personnelles soient collectées à cette fin.
                    </p>
                  }
                />
              );
            })}
          {structures &&
            structures?.map((structure, index) => {
              const currentStructure = structuresMapping?.get(structure.id);

              if (!currentStructure) {
                return null;
              }

              return (
                <FormCheckbox
                  key={structure.id}
                  control={transactionForm.control}
                  name={`structures.${index}.hasConsented`}
                  className="mt-2"
                  labelClassName="text-sm"
                  labelContent={
                    <p>
                      En cochant cette case, j&apos;accepte de recevoir des
                      offres promotionnelles par courrier électronique de la
                      part de {currentStructure.name} et que mes données
                      personnelles soient collectées à cette fin.
                    </p>
                  }
                />
              );
            })}
        </Form>
      </div>
    </div>
  );
}

export default CheckoutCart;
